"use client";

import { useEffect, useState } from "react";

// ─── Data Sources ────────────────────────────────────────────

const OM_URL = "https://api.open-meteo.com/v1/forecast?latitude=50.267&longitude=-119.272&current=temperature_2m,relative_humidity_2m,dew_point_2m,wind_speed_10m,wind_gusts_10m,cloud_cover,pressure_msl&hourly=precipitation_probability&models=gem_seamless&elevation=482&timezone=America%2FVancouver&forecast_days=1&temperature_unit=celsius";
const EC_URL = "https://api.weather.gc.ca/collections/swob-realtime/items?url=CWJV&sortby=-date_tm-value&limit=1&f=json";
const EC_FEED_URL = `/api/proxy?url=${encodeURIComponent("https://weather.gc.ca/rss/city/bc-3_e.xml")}`;
function pickObs(props, keys) {
  for (const k of keys) {
    const v = parseFloat(props?.[k]);
    if (Number.isFinite(v)) return v;
  }
  return null;
}

// Fixed Text-to-Data Mapper (Order of operations matters)
function mapConditionToCloudCover(conditionText) {
  if (!conditionText) return null;
  const text = conditionText.toLowerCase();

  // 1. Check specific modifiers first
  if (text.includes("mainly sunny") || text.includes("mostly sunny")) return 15;
  if (text.includes("mainly clear")) return 15;
  if (text.includes("partly cloudy") || text.includes("mix of sun and cloud")) return 40;
  if (text.includes("cloudy periods")) return 50;
  if (text.includes("mostly cloudy") || text.includes("mainly cloudy")) return 75;
  if (text.includes("few clouds")) return 10;
  if (text.includes("overcast")) return 100;

  // 2. Base conditions act as the catch-all
  if (text.includes("cloudy")) return 100;
  if (text.includes("sunny")) return 0;
  if (text.includes("clear")) return 0;
  return null;
}

// ─── Client-Safe Moon Phase Engine ───────────────────────────

function getMoonPhase(date = new Date()) {
  const SYNODIC = 29.53058867;
  const ref = Date.UTC(2000, 0, 6, 18, 14, 0);
  const days = (date.getTime() - ref) / 86400000;
  const phase = (days % SYNODIC + SYNODIC) % SYNODIC / SYNODIC;
  const illumination = Math.round((1 - Math.cos(2 * Math.PI * phase)) / 2 * 100);
  const names = ["NEW MOON", "WAXING CRESCENT", "FIRST QUARTER", "WAXING GIBBOUS", "FULL MOON", "WANING GIBBOUS", "LAST QUARTER", "WANING CRESCENT"];
  const idx = Math.round(phase * 8) % 8;
  return {
    phase,
    illumination,
    name: names[idx],
    waxing: phase < 0.5
  };
}
function MoonIcon({
  phase,
  size = 44
}) {
  if (phase == null) return null;
  const r = size / 2 - 2;
  const cx = size / 2;
  const cy = size / 2;
  const cosP = Math.cos(2 * Math.PI * phase);
  const rx = Math.abs(cosP) * r;
  const waxing = phase < 0.5;
  const gibbous = phase > 0.25 && phase < 0.75;
  const limbSweep = waxing ? 1 : 0;
  const termSweep = gibbous ? waxing ? 1 : 0 : waxing ? 0 : 1;
  const d = `M ${cx} ${cy - r}
             A ${r} ${r} 0 0 ${limbSweep} ${cx} ${cy + r}
             A ${rx} ${r} 0 0 ${termSweep} ${cx} ${cy - r} Z`;
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="hsla(240, 10%, 20%, 0.8)" />
      <path d={d} fill="#e8e6e1" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="hsla(0,0%,100%,0.15)" strokeWidth="1" />
    </svg>;
}

// ─── Data Hooks ──────────────────────────────────────────────

function useObservatoryTelemetry() {
  const [wx, setWx] = useState(null);
  const [kp, setKp] = useState(null);
  const [moon, setMoon] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setMoon(getMoonPhase());
    async function fetchWx() {
      try {
        setError(false); // Fix: Allow the widget to auto-heal if network connection drops and returns

        const [om, ec, feedResponse] = await Promise.all([fetch(OM_URL).then(r => r.json()), fetch(EC_URL).then(r => r.json()).catch(() => null), fetch(EC_FEED_URL).then(r => r.text()).catch(err => {
          console.error("EC RSS Proxy fetch failed:", err);
          return null;
        })]);
        const hourKey = (om.current.time ?? "").slice(0, 13);
        const hourIdx = om.hourly.time.findIndex(t => t.startsWith(hourKey));
        const precipProb = hourIdx >= 0 ? om.hourly.precipitation_probability[hourIdx] ?? 0 : 0;
        const next = {
          temp: Math.round(om.current.temperature_2m * 10) / 10,
          humidity: om.current.relative_humidity_2m,
          dewpoint: Math.round(om.current.dew_point_2m * 10) / 10,
          windspeed: Math.round(om.current.wind_speed_10m),
          gusts: Math.round(om.current.wind_gusts_10m),
          cloud_cover: om.current.cloud_cover,
          pressure: Math.round(om.current.pressure_msl),
          precip_prob: precipProb,
          ec_condition_text: ""
        };
        if (feedResponse) {
          let rawXml = feedResponse;
          try {
            const parsedJson = JSON.parse(feedResponse);
            rawXml = parsedJson.data ?? parsedJson.body ?? feedResponse;
          } catch {
            // Safe fallback if the proxy returns pure text rather than JSON
          }
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(rawXml, "text/xml");
          const title = xmlDoc.querySelector("item title")?.textContent || xmlDoc.querySelector("entry title")?.textContent || "";
          const conditionMatch = title.match(/Current Conditions:\s*([^,]+)/i);
          if (conditionMatch?.[1]) {
            const parsedText = conditionMatch[1].trim();
            next.ec_condition_text = parsedText.toUpperCase();
            const calculatedCloud = mapConditionToCloudCover(parsedText);
            if (calculatedCloud !== null) {
              next.cloud_cover = calculatedCloud;
            }
          }
        }
        const props = ec?.features?.[0]?.properties;
        if (props) {
          const t = pickObs(props, ["air_temp"]);
          const dp = pickObs(props, ["dwpt_temp"]);
          const rh = pickObs(props, ["rel_hum"]);
          const ws = pickObs(props, ["avg_wnd_spd_10m_pst10mts", "avg_wnd_spd_10m_pst2mts", "avg_wnd_spd_10m_pst1mt", "avg_wnd_spd_10m_pst1hr"]);
          const gs = pickObs(props, ["max_wnd_spd_10m_pst10mts", "max_wnd_spd_10m_pst1mt", "max_wnd_spd_10m_pst1hr"]);
          const sp = pickObs(props, ["stn_pres"]);
          if (t != null) next.temp = Math.round(t * 10) / 10;
          if (dp != null) next.dewpoint = Math.round(dp * 10) / 10;
          if (rh != null) next.humidity = Math.round(rh);
          if (ws != null) next.windspeed = Math.round(ws);
          if (gs != null) next.gusts = Math.round(gs);
          if (sp != null && t != null) {
            const h = 484;
            const mslp = sp * Math.pow(1 - 0.0065 * h / (t + 0.0065 * h + 273.15), -5.257);
            next.pressure = Math.round(mslp);
          }
        }
        next.gusts = Math.max(next.gusts, next.windspeed);
        setWx(next);
      } catch (err) {
        console.error("Telemetry engine crashed:", err);
        setError(true);
      }
    }
    async function fetchKp() {
      try {
        let res;
        try {
          res = await fetch("https://services.swpc.noaa.gov/products/noaa-planetary-k-index.json");
          if (!res.ok) throw new Error("CORS fallback triggered");
        } catch {
          res = await fetch(`/api/proxy?url=${encodeURIComponent("https://services.swpc.noaa.gov/products/noaa-planetary-k-index.json")}`);
        }
        let json = await res.json();
        if (typeof json === "string") json = JSON.parse(json);
        if (json && !Array.isArray(json)) json = json.data ?? json.body ?? json;
        if (!Array.isArray(json) || json.length === 0) return setKp(null);
        const last = json[json.length - 1];
        let value = null;
        if (Array.isArray(last)) value = parseFloat(last[1]);else if (last && typeof last === "object") value = parseFloat(last.Kp ?? last.kp_index ?? last.kp);
        setKp(Number.isFinite(value) ? value : null);
      } catch {
        setKp(null);
      }
    }
    fetchWx();
    fetchKp();
    const wxInterval = setInterval(fetchWx, 15 * 60 * 1000);
    const kpInterval = setInterval(fetchKp, 15 * 60 * 1000);
    return () => {
      clearInterval(wxInterval);
      clearInterval(kpInterval);
    };
  }, []);
  return {
    wx,
    kp,
    moon,
    error
  };
}

// ─── Status Color Assets ─────────────────────────────────────

const GREEN = "#4ade80";
const AMBER = "#fbbf24";
const RED = "#f87171";
function kpStatus(kp) {
  if (kp == null) return {
    color: "#71717a",
    label: "NO DATA"
  };
  if (kp < 4) return {
    color: GREEN,
    label: "QUIET"
  };
  if (kp < 5) return {
    color: AMBER,
    label: "ACTIVE"
  };
  return {
    color: RED,
    label: "STORM"
  };
}
function cloudStatus(cc) {
  if (cc < 20) return {
    color: GREEN,
    label: "CLEAR"
  };
  if (cc < 50) return {
    color: AMBER,
    label: "PARTLY CLOUDY"
  };
  if (cc < 80) return {
    color: AMBER,
    label: "MOSTLY CLOUDY"
  };
  return {
    color: RED,
    label: "OVERCAST"
  };
}
function dewStatus(temp, dewpoint) {
  const spread = temp - dewpoint;
  if (spread > 5) return {
    color: GREEN,
    label: "DRY OPTICS"
  };
  if (spread > 2) return {
    color: AMBER,
    label: "DEW RISK"
  };
  return {
    color: RED,
    label: "DEW LIKELY"
  };
}
function windStatus(wind) {
  if (wind < 10) return {
    color: GREEN,
    label: "STEADY"
  };
  if (wind < 20) return {
    color: AMBER,
    label: "BREEZY"
  };
  return {
    color: RED,
    label: "UNSTABLE"
  };
}

// ─── Instrument Layout Shield ────────────────────────────────

function Instrument({
  label,
  value,
  unit,
  status,
  detail,
  children
}) {
  return <div className="flex flex-col gap-3 border border-secondary/20 rounded-md p-5 md:p-6" style={{
    backgroundColor: "rgba(15, 17, 21, 0.6)",
    backdropFilter: "blur(24px)"
  }}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/40">
          {label}
        </span>
        {status && <span className="size-1.5 animate-pulse" style={{
        backgroundColor: status.color
      }} />}
      </div>
      {children ? children : <div className="flex items-baseline gap-1.5">
          <span className="font-default font-bold text-3xl md:text-4xl text-foreground tabular-nums leading-none">
            {value}
          </span>
          {unit && <span className="font-mono text-xs text-foreground/50 uppercase">{unit}</span>}
        </div>}
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
        {status && <span style={{
        color: status.color
      }}>{status.label}</span>}
        {detail && <span className="text-foreground/40">{detail}</span>}
      </div>
    </div>;
}

// ─── Main Output Component ───────────────────────────────────

export default function ObservatoryTelemetry() {
  const {
    wx,
    kp,
    moon,
    error
  } = useObservatoryTelemetry();
  if (error) return null;
  const imagingGood = wx && wx.cloud_cover < 30 && wx.precip_prob < 20 && wx.windspeed < 20;
  return <section id="observatory-telemetry" className="relative w-full bg-background text-foreground dark py-20 md:py-28 px-6 md:px-10 lg:px-16">
      <div className="flex items-center gap-3 mb-4">
        <span className="size-1.5 bg-secondary animate-pulse" />
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-secondary">
          02 — OBSERVATORY TELEMETRY
        </span>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
        <h2 className="font-default font-bold uppercase leading-[0.9] text-foreground" style={{
        fontSize: "clamp(2rem, 5vw, 4rem)",
        letterSpacing: "-0.04em"
      }}>
          LIVE SITE CONDITIONS
        </h2>
        {wx && <div className="font-mono text-[11px] uppercase tracking-[0.25em] whitespace-nowrap" style={{
        color: imagingGood ? GREEN : RED
      }}>
            {imagingGood ? "▲ IMAGING CONDITIONS NOMINAL" : "▼ IMAGING CONDITIONS POOR"}
          </div>}
      </div>

      {!wx || !moon ? <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-foreground/30 animate-pulse">
          ACQUIRING TELEMETRY...
        </div> : <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {/* Sky Cover */}
          <Instrument label="SKY COVER" value={wx.cloud_cover} unit="%" status={cloudStatus(wx.cloud_cover)} detail={wx.ec_condition_text || `PRECIP ${wx.precip_prob}%`} />

          {/* Geomagnetic Index */}
          <Instrument label="KP INDEX" value={kp != null ? kp.toFixed(2) : "––"} status={kpStatus(kp)} detail="NOAA SWPC" />

          {/* Lunar Phase */}
          <Instrument label="LUNAR PHASE" status={{
        color: moon.illumination < 30 ? GREEN : moon.illumination < 70 ? AMBER : RED,
        label: moon.name
      }} detail={`${moon.illumination}% ILLUM`}>
            <div className="flex items-center gap-4">
              <MoonIcon phase={moon.phase} />
              <span className="font-default font-bold text-3xl md:text-4xl text-foreground tabular-nums leading-none">
                {moon.illumination}
                <span className="font-mono text-xs text-foreground/50 ml-1">%</span>
              </span>
            </div>
          </Instrument>

          {/* Wind Dynamics */}
          <Instrument label="WIND" value={wx.windspeed} unit="KM/H" status={windStatus(wx.windspeed)} detail={`GUSTS ${wx.gusts}`} />

          {/* Ambient Temp */}
          <Instrument label="AMBIENT TEMP" value={wx.temp} unit="°C" detail={`${wx.pressure} HPA MSL`} />

          {/* Dew Point / Spread */}
          <Instrument label="DEW POINT" value={wx.dewpoint} unit="°C" status={dewStatus(wx.temp, wx.dewpoint)} detail={`SPREAD ${(wx.temp - wx.dewpoint).toFixed(1)}°`} />

          {/* Relative Humidity */}
          <Instrument label="REL HUMIDITY" value={wx.humidity} unit="%" />

          {/* Site Coordinates */}
          <Instrument label="SITE" detail="BORTLE 5">
            <div className="flex flex-col gap-1">
              <span className="font-default font-bold text-xl md:text-2xl text-foreground leading-tight">
                VERNON BC
              </span>
              <span className="font-mono text-[10px] text-foreground/40 tracking-[0.15em]">
                50.267°N / 119.272°W
              </span>
            </div>
          </Instrument>
        </div>}
    </section>;
}